import moment from 'moment';

import { VIEW_ENTITY_TYPES, type QuotesOverviewRow } from '@frontend/api';

import {
  ARRAY_SEPARATOR,
  COLUMN_PARAMETER_KEY,
  DASHBOARD_LINK_PREVIEW,
  ENTITY_PARAMETER_KEY,
  ORDER_PARAMETER_KEY,
} from 'modules/views/const';

export type OverviewColumnId = keyof Omit<QuotesOverviewRow, 'userGroup'>;
export type OverviewEntity =
  (typeof VIEW_ENTITY_TYPES)[keyof typeof VIEW_ENTITY_TYPES];

const QUOTES_ROUTE = '/quotes';

// ---------------------------------------------------------------------------
// Column sets — comma-separated query param names per the design spec.
// Part Thumbnail is a fixed column (not controlled via the `columns` param).
// ---------------------------------------------------------------------------

// Quote entity column sets, named after the views that use them.
const QUOTE_COLS = {
  DUE_AND_ON_HOLD: [
    'number',
    'rfq_number',
    'priority',
    'account',
    'contact',
    'salesperson',
    'estimator',
    'internal_quotes',
    'quote_item_status',
    'workflow_steps',
    'quote_items_count',
    'price',
    'due',
  ].join(ARRAY_SEPARATOR),

  NOT_STARTED: [
    'number',
    'rfq_number',
    'priority',
    'account',
    'contact',
    'salesperson',
    'estimator',
    'internal_quotes',
    'quote_items_count',
    'created',
    'due',
  ].join(ARRAY_SEPARATOR),

  CREATED: [
    'number',
    'rfq_number',
    'quote_status',
    'priority',
    'account',
    'contact',
    'salesperson',
    'estimator',
    'internal_quotes',
    'quote_item_status',
    'workflow_steps',
    'quote_items_count',
    'created',
    'due',
  ].join(ARRAY_SEPARATOR),

  SENT: [
    'number',
    'rfq_number',
    'quote_status',
    'priority',
    'account',
    'contact',
    'salesperson',
    'estimator',
    'internal_quotes',
    'quote_items_count',
    'price',
    'sent',
    'viewed',
    'expired',
    'ordered',
  ].join(ARRAY_SEPARATOR),

  OUTSTANDING: [
    'number',
    'rfq_number',
    'quote_status',
    'priority',
    'account',
    'contact',
    'salesperson',
    'estimator',
    'internal_quotes',
    'quote_items_count',
    'price',
    'sent',
    'viewed',
    'expired',
  ].join(ARRAY_SEPARATOR),
} as const;

// Line item entity column sets, named after the views that use them.
const LINE_ITEM_COLS = {
  DUE_AND_ON_HOLD: [
    'part_number',
    'part_description',
    'quote_number',
    'estimator',
    'internal_quotes',
    'quote_item_status',
    'workflow_steps',
    'process',
    'material',
    'finish',
    'priority',
    'account',
    'contact',
    'quantities',
    'price',
    'due',
  ].join(ARRAY_SEPARATOR),

  NOT_STARTED: [
    'part_number',
    'part_description',
    'quote_number',
    'estimator',
    'internal_quotes',
    'priority',
    'account',
    'contact',
    'created',
    'due',
  ].join(ARRAY_SEPARATOR),

  CREATED: [
    'part_number',
    'part_description',
    'quote_number',
    'estimator',
    'internal_quotes',
    'quote_item_status',
    'workflow_steps',
    'process',
    'material',
    'finish',
    'priority',
    'account',
    'contact',
    'created',
    'due',
  ].join(ARRAY_SEPARATOR),

  SENT: [
    'part_number',
    'part_description',
    'quote_number',
    'internal_quotes',
    'priority',
    'account',
    'contact',
    'quantities',
    'price',
    'sent',
    'viewed',
    'expired',
    'ordered',
  ].join(ARRAY_SEPARATOR),

  OUTSTANDING: [
    'part_number',
    'part_description',
    'quote_number',
    'internal_quotes',
    'priority',
    'account',
    'contact',
    'quantities',
    'price',
    'sent',
    'viewed',
    'expired',
  ].join(ARRAY_SEPARATOR),
} as const;

// ---------------------------------------------------------------------------
// Filter param keys
// The filter system reads values via decodeURIComponent(search.entries()),
// so filter values must be double-encoded (encodeURIComponent before params.set).
// Non-filter params (entity, preview, columns, o) are read via search.get()
// and only need standard URLSearchParams encoding.
// ---------------------------------------------------------------------------

const K = {
  QUOTE_STATUS: 'quote_status_in',
  QUOTE_ITEM_STATUS: 'quote_item_status_in',
  CREATED_PREVIOUS: 'created_previous',
  CREATED_CURRENT: 'created_current',
  CREATED_RANGE: 'created_range',
  DUE_BEFORE: 'due_before',
  SENT_CURRENT: 'sent_current',
  SENT_PREVIOUS: 'sent_previous',
} as const;

// Pre-encoded status filter values
const S = {
  DRAFT: encodeURIComponent('draft'),
  ALL: encodeURIComponent(
    [
      'draft',
      'outstanding',
      'expired',
      'cancelled',
      'accepted',
      'partially_accepted',
      'lost',
    ].join(ARRAY_SEPARATOR),
  ),
  OUTSTANDING: encodeURIComponent('outstanding'),
  OUTSTANDING_EXPIRED: encodeURIComponent(
    ['outstanding', 'expired'].join(ARRAY_SEPARATOR),
  ),
  NOT_STARTED: encodeURIComponent('not_started'),
  ON_HOLD: encodeURIComponent('on_hold'),
} as const;

// Static relative date filter values (no runtime computation needed)
const D = {
  LAST_90_DAYS: encodeURIComponent('90_days_inclusive'),
  THIS_WEEK: encodeURIComponent('week'),
  LAST_7_DAYS: encodeURIComponent('7_days_inclusive'),
} as const;

// Dynamic date values (computed fresh on each call so the URL reflects today)
const upcomingSaturday = (): string => {
  const daysUntilSat = (6 - moment().day() + 7) % 7 || 7; // if today is Sat, advance to next Sat
  return encodeURIComponent(
    moment().add(daysUntilSat, 'days').endOf('day').format(),
  );
};

const sevenDaysFromNow = (): string =>
  encodeURIComponent(moment().add(7, 'days').endOf('day').format());

const created90To3DaysRange = (): string => {
  const start = moment().subtract(90, 'days').startOf('day').format();
  const end = moment().subtract(3, 'days').endOf('day').format();
  return encodeURIComponent(`${start}${ARRAY_SEPARATOR}${end}`);
};

// ---------------------------------------------------------------------------
// View URL params map
// ---------------------------------------------------------------------------

// Param values may be a plain string (static) or a zero-arg function
// (dynamic — called fresh at URL-build time so date values reflect today).
type ParamValue = string | (() => string);
type ViewParams = Record<string, ParamValue>;
type ViewParamsMap = Record<
  OverviewColumnId,
  Record<OverviewEntity, ViewParams>
>;

const VIEW_URL_PARAMS: ViewParamsMap = {
  dueThisWeek: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.DUE_BEFORE]: upcomingSaturday,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.DUE_BEFORE]: upcomingSaturday,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
    },
  },

  dueNextSevenDays: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.DUE_BEFORE]: sevenDaysFromNow,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.DUE_BEFORE]: sevenDaysFromNow,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
    },
  },

  notStarted: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.NOT_STARTED,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.CREATED_RANGE]: created90To3DaysRange,
      [K.QUOTE_ITEM_STATUS]: S.NOT_STARTED,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.NOT_STARTED,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.CREATED_RANGE]: created90To3DaysRange,
      [K.QUOTE_ITEM_STATUS]: S.NOT_STARTED,
    },
  },

  onHold: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.QUOTE_ITEM_STATUS]: S.ON_HOLD,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.DUE_AND_ON_HOLD,
      [ORDER_PARAMETER_KEY]: 'due',
      [K.QUOTE_STATUS]: S.DRAFT,
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.QUOTE_ITEM_STATUS]: S.ON_HOLD,
    },
  },

  createdThisWeek: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.CREATED,
      [ORDER_PARAMETER_KEY]: '-created',
      [K.QUOTE_STATUS]: S.ALL,
      [K.CREATED_CURRENT]: D.THIS_WEEK,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.CREATED,
      [ORDER_PARAMETER_KEY]: '-created',
      [K.QUOTE_STATUS]: S.ALL,
      [K.CREATED_CURRENT]: D.THIS_WEEK,
    },
  },

  createdLastSevenDays: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.CREATED,
      [ORDER_PARAMETER_KEY]: '-created',
      [K.QUOTE_STATUS]: S.ALL,
      [K.CREATED_PREVIOUS]: D.LAST_7_DAYS,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.CREATED,
      [ORDER_PARAMETER_KEY]: '-created',
      [K.QUOTE_STATUS]: S.ALL,
      [K.CREATED_PREVIOUS]: D.LAST_7_DAYS,
    },
  },

  sentThisWeek: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.SENT,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.SENT_CURRENT]: D.THIS_WEEK,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.SENT,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.SENT_CURRENT]: D.THIS_WEEK,
    },
  },

  sentLastSevenDays: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.SENT,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.SENT_PREVIOUS]: D.LAST_7_DAYS,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.SENT,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.SENT_PREVIOUS]: D.LAST_7_DAYS,
    },
  },

  outstanding: {
    [VIEW_ENTITY_TYPES.QUOTES]: {
      [COLUMN_PARAMETER_KEY]: QUOTE_COLS.OUTSTANDING,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.QUOTE_STATUS]: S.OUTSTANDING,
    },
    [VIEW_ENTITY_TYPES.QUOTE_ITEMS]: {
      [COLUMN_PARAMETER_KEY]: LINE_ITEM_COLS.OUTSTANDING,
      [ORDER_PARAMETER_KEY]: '-sent',
      [K.CREATED_PREVIOUS]: D.LAST_90_DAYS,
      [K.QUOTE_STATUS]: S.OUTSTANDING_EXPIRED,
    },
  },
};

// Static set of valid column IDs — used to validate the `preview` URL param
// before calling buildQuotesOverviewCellUrl, preventing a crash when the param
// is malformed (e.g. a stale URL after a column rename).
const VALID_OVERVIEW_COLUMN_IDS: ReadonlySet<string> =
  new Set<OverviewColumnId>([
    'dueThisWeek',
    'dueNextSevenDays',
    'notStarted',
    'onHold',
    'createdThisWeek',
    'createdLastSevenDays',
    'sentThisWeek',
    'sentLastSevenDays',
    'outstanding',
  ]);

export function isValidOverviewColumnId(id: string): id is OverviewColumnId {
  return VALID_OVERVIEW_COLUMN_IDS.has(id);
}

/**
 * Builds a `/quotes` URL for a specific cell in the Quotes Overview pane.
 *
 * Any dynamic param values (e.g. upcoming Saturday, 90-day range) are
 * resolved fresh at call time so the URL always reflects the current date.
 *
 * Filter param values are double-encoded to match the convention used by
 * `transformSearchToInternalFilterState`, which applies `decodeURIComponent`
 * on top of the standard `URLSearchParams` decode.
 */
export const buildQuotesOverviewCellUrl = (
  columnId: OverviewColumnId,
  entity: OverviewEntity,
): string => {
  const viewParams = VIEW_URL_PARAMS[columnId][entity];
  const resolved = Object.fromEntries(
    Object.entries(viewParams).map(([k, v]) => [
      k,
      typeof v === 'function' ? v() : v,
    ]),
  );
  return `${QUOTES_ROUTE}?${new URLSearchParams({
    [ENTITY_PARAMETER_KEY]: entity,
    // Encode the source cell so PageState can rebuild the canonical URL for change-detection
    [DASHBOARD_LINK_PREVIEW]: `${columnId}:${entity}`,
    ...resolved,
  })}`;
};
